import React, { useState, useRef, useEffect } from "react";
import "./UploadVideo.css";
import "../../Components/Header/Header.css";
import Header from "./../../Components/Header/Header";
import ConfirmIconLogo from "../../assets/images/ConfirmIconLogo.png";
import SuccessIcon from "../../assets/images/SuccessIcon.png";
import ArrowRight from "../../assets/images/arrow-right-circle.svg";
import ThumbnailDeleteIcon from "../../assets/images/ThumbnailDeleteIcon.svg";
import ConfirmDelete from "../../assets/images/ConfirmDelete.svg";
import { useNavigate } from "react-router-dom";
import { callApi } from "../../utils/apiService.js";
import { videoCreatorEndPoint } from "../../Constants/constants.js";
import { useUser } from "../../Context/UserContext.jsx";
import Select from "react-select";
import { showToast } from "../../utils/toastUtils.js";

export default function UploadVideo() {
  const [isUploading, setIsUploading] = useState(false);
  const [videoFile, setVideoFile] = useState(null);
  const [youtubeLink, setYoutubeLink] = useState("");
  const [thumbnail, setThumbnail] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [caption, setCaption] = useState("");
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [keywordInput, setKeywordInput] = useState("");
  const [keywords, setKeywords] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [videoType, setVideoType] = useState("");
  const [language, setLanguage] = useState("");
  const [contentCategory, setContentCategory] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [videoPreview, setVideoPreview] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [thumbnailUploadProgress, setThumbnailUploadProgress] = useState(0);
  const [videoUploadComplete, setVideoUploadComplete] = useState(false);
  const [showRemoveVideo, setShowRemoveVideo] = useState(false);
  const [showDeleteThumbnailModal, setShowDeleteThumbnailModal] =
    useState(false);
  const [videoTypes, setVideoTypes] = useState([]);
  const [videoDuration, setVideoDuration] = useState(0);
  const [errors, setErrors] = useState({});
  const [previousVideos, setPreviousVideos] = useState([]);
  const [videoName, setVideoName] = useState("");
  const [videoFileType, setVideoFileType] = useState("");
  const [videoSize, setVideoSize] = useState("");
  const [thumbnailName, setThumbnailName] = useState("");
  const [thumbnailSize, setThumbnailSize] = useState("");
  const [thumbnailFileType, setThumbnailFileType] = useState("");
  const [unsupportedPreviewFile, setUnsupportedPreviewFile] = useState(false);
  const [showCategoryHelp, setShowCategoryHelp] = useState(false);

  const videoInputRef = useRef(null);
  const thumbnailInputRef = useRef(null);
  const navigate = useNavigate();
  const { languages } = useUser();

  useEffect(() => {
    const savedVideos = sessionStorage.getItem("previousVideos");

    if (savedVideos) {
      setPreviousVideos(JSON.parse(savedVideos));
    }
  }, []);

  useEffect(() => {
    const handleFocus = () => {
      const savedVideos = sessionStorage.getItem("previousVideos");

      if (savedVideos) {
        setPreviousVideos(JSON.parse(savedVideos));
      }
    };

    window.addEventListener("focus", handleFocus);

    return () => {
      window.removeEventListener("focus", handleFocus);
    };
  }, []);

  const fetchVideoTypes = async () => {
    try {
      const response = await callApi(
        `${videoCreatorEndPoint}video/types/list`,
        "GET",
      );
      if (response?.status) {
        setVideoTypes(response?.data || []);
      }
    } catch (err) { }
  };

  useEffect(() => {
    fetchVideoTypes();
  }, []);

  useEffect(() => {
    if (!showCategoryHelp) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setShowCategoryHelp(false);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showCategoryHelp]);

  const validateForm = () => {
    const newErrors = {};

    if (!videoName) {
      newErrors.videoName = "Video file is required";
    }

    if (keywords.length === 0) {
      newErrors.keywords = "Please add at least one keyword";
    }

    if (!thumbnailName) {
      newErrors.thumbnailName = "Thumbnail is required";
    }

    if (!title.trim()) {
      newErrors.title = "Video title is required";
    }

    if (!description.trim()) {
      newErrors.description = "Video description is required";
    }

    if (!caption.trim()) {
      newErrors.caption = "Caption is required";
    }

    if (!videoType) {
      newErrors.videoType = "Please select a video type";
    }

    if (!language) {
      newErrors.language = "Please select a video language";
    }

    if (!contentCategory) {
      newErrors.contentCategory = "Please select a content category";
    }

    if (!confirmChecked) {
      newErrors.consent = "Please confirm ownership of the content";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };
  const handleKeywordKeyDown = (e) => {
    if (e.key === "Enter" && keywordInput.trim()) {
      e.preventDefault();

      if (editingIndex !== null) {
        const updated = [...keywords];
        updated[editingIndex] = keywordInput.trim();
        setKeywords(updated);
        setEditingIndex(null);
      } else {
        setKeywords([...keywords, keywordInput.trim()]);
      }
      setErrors((prev) => ({
        ...prev,
        keywords: "",
      }));

      setKeywordInput("");
    }
  };

  const removeKeyword = (index) => {
    setKeywords(keywords.filter((_, i) => i !== index));
  };

  const editKeyword = (index) => {
    setKeywordInput(keywords[index]);
    setEditingIndex(index);
  };
  const handleVideoDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const file = e.dataTransfer.files[0];

    if (file && file.type.startsWith("video/")) {
      setVideoFile(file);
      setVideoSize(file?.size);
      setVideoName(file?.name?.split(".").slice(0, -1).join("."));
      setVideoFileType(file?.type);
      setVideoPreview(URL.createObjectURL(file));

      const video = document.createElement("video");
      video.preload = "metadata";

      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        setVideoDuration(Math.round(video.duration));
      };

      video.src = URL.createObjectURL(file);

      simulateUploadProgress();

      setErrors((prev) => ({
        ...prev,
        videoName: "",
      }));
    }
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];

    if (file && file.type.startsWith("video/")) {
      setVideoFile(file);
      setVideoSize(file?.size);
      setVideoName(file?.name?.split(".").slice(0, -1).join("."));
      setVideoFileType(file?.type);
      setVideoPreview(URL.createObjectURL(file));

      const video = document.createElement("video");
      video.preload = "metadata";

      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        setVideoDuration(Math.round(video.duration));
      };

      video.src = URL.createObjectURL(file);

      simulateUploadProgress();

      setErrors((prev) => ({
        ...prev,
        videoName: "",
      }));
    }
  };
  const simulateThumbnailUploadProgress = () => {
    setThumbnailUploadProgress(0);

    const interval = setInterval(() => {
      setThumbnailUploadProgress((prev) => {
        const next = Math.min(prev + Math.random() * 30, 100);

        if (next === 100) {
          clearInterval(interval);
        }

        return next;
      });
    }, 500);
  };
  const handleThumbnailDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const allowedThumbnailTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
    ];
    const file = e.dataTransfer.files[0];
    if (file && allowedThumbnailTypes.includes(file.type)) {
      setThumbnail(file);
      setThumbnailSize(file?.size);
      setThumbnailName(file?.name?.split(".").slice(0, -1).join("."));
      setThumbnailFileType(file?.type);
      setThumbnailPreview(URL.createObjectURL(file));
      setErrors((prev) => ({
        ...prev,
        thumbnailName: "",
      }));
      simulateThumbnailUploadProgress();
    }
    else {
      setErrors((prev) => ({
        ...prev,
        thumbnailName: "Only JPG, JPEG and PNG files are allowed",
      }));
    }
  };

  const handleThumbnailChange = (e) => {
    const allowedThumbnailTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
    ];
    const file = e.target.files[0];
    if (file && allowedThumbnailTypes.includes(file.type)) {
      setThumbnail(file);
      setThumbnailSize(file?.size);
      setThumbnailName(file?.name?.split(".").slice(0, -1).join("."));
      setThumbnailFileType(file?.type);
      setThumbnailPreview(URL.createObjectURL(file));
      setErrors((prev) => ({
        ...prev,
        thumbnailName: "",
      }));
      simulateThumbnailUploadProgress();
    }

    else {
      setErrors((prev) => ({
        ...prev,
        thumbnailName: "Only JPG, JPEG and PNG files are allowed",
      }));
    }
  };
  const simulateUploadProgress = () => {
    setVideoUploadComplete(false);
    setShowRemoveVideo(false);

    setUploadProgress(0);
    const interval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          setVideoUploadComplete(true);
          setShowRemoveVideo(true);
          return 100;
        }
        const next = prev + Math.random() * 30;
        return next >= 100 ? 100 : next;
      });
    }, 1000);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handlePublish = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setShowConfirmModal(true);
  };

  const handleConfirmSubmit = async () => {

    setIsUploading(true);
    try {
      const payload = {
        videoTypeId: videoType,
        videoLanguageId: language,
        title,
        description,
        caption,
        videoDuration,
        videoFileType,
        videoSize,
        videoName,
        thumbnailName,
        thumbnailFileType,
        thumbnailSize,
        keywords,
        previousVideos: previousVideos.map((item) => ({
          timeStamp: item.timestampInSeconds,
          videoId: item.videoId,
        })),
        contentCategory,
      };

      const response = await callApi(
        `${videoCreatorEndPoint}video/create`,
        "POST",
        payload,
      );

      if (response?.status) {
        const { videoUploadUrl, thumbnailUploadUrl } = response.data;
        await fetch(videoUploadUrl, {
          method: "PUT",
          body: videoFile,
        });

        await fetch(thumbnailUploadUrl, {
          method: "PUT",
          body: thumbnail,
        });

        setShowConfirmModal(false);
        setShowSuccessModal(true);

        setTimeout(() => {
          setVideoFile(null);
          setVideoName("");
          setVideoFileType("");
          setVideoSize("");
          setThumbnailName("");
          setThumbnailFileType("");
          setThumbnailSize("");
          setVideoPreview(null);
          setYoutubeLink("");
          setThumbnail(null);
          setThumbnailPreview(null);
          setTitle("");
          setDescription("");
          setCaption("");
          setKeywords([]);
          setKeywordInput("");
          setEditingIndex(null);
          setVideoType("");
          setLanguage("");
          setContentCategory("");
          setUploadProgress(0);
        }, 2000);
      } else {
        setShowConfirmModal(false);
        showToast(
          "error",
          response?.message || response?.alert || "Failed to create video. Please try again.",
        );
      }
    } catch (error) {
      console.error("Video creation failed:", error);
      setShowConfirmModal(false);
      showToast(
        "error",
        error?.response?.data?.message ||
          error?.message ||
          "Failed to create video. Please try again.",
      );
    } finally {
      setIsUploading(false);
    }
  };
  const handleCancel = () => {
    if (
      window.confirm(
        "Are you sure you want to cancel? All changes will be lost.",
      )
    ) {
      sessionStorage.removeItem("uploadVideoDraft");
      sessionStorage.removeItem("previousVideos");
      setVideoFile(null);
      setVideoName("");
      setVideoFileType("");
      setVideoSize("");
      setThumbnailName("");
      setThumbnailFileType("");
      setThumbnailSize("");
      setVideoPreview(null);
      setYoutubeLink("");
      setThumbnail(null);
      setThumbnailPreview(null);
      setTitle("");
      setDescription("");
      setKeywords([]);
      setKeywordInput("");
      setEditingIndex(null);
      setCaption("");
      setVideoType("");
      setLanguage("");
      setContentCategory("");
      setUploadProgress(0);
    }
  };

  const handleDone = () => {
    setShowSuccessModal(false);
    sessionStorage.removeItem("uploadVideoDraft");
    sessionStorage.removeItem("previousVideos");
    window.location.href = "/dashboard";
  };
  const handleview = () => {
    setShowSuccessModal(false);
    sessionStorage.removeItem("uploadVideoDraft");
    sessionStorage.removeItem("previousVideos");
    window.location.href = "/view-awaiting-list";
  };

  const handleDeleteThumbnail = () => {
    setThumbnail(null);
    setThumbnailName("");
    setThumbnailFileType("");
    setThumbnailSize("");
    setThumbnailPreview(null);
    setThumbnailUploadProgress(0);
    setShowDeleteThumbnailModal(false);

    if (thumbnailInputRef.current) {
      thumbnailInputRef.current.value = "";
    }
  };

  useEffect(() => {
    const draft = sessionStorage.getItem("uploadVideoDraft");

    if (draft) {
      const data = JSON.parse(draft);

      setTitle(data.title || "");
      setDescription(data.description || "");
      setCaption(data.caption || "");
      setKeywords(data.keywords || []);
      setVideoType(data.videoType || "");
      setLanguage(data.language || "");
      setContentCategory(data.contentCategory || "");
      setConfirmChecked(data.confirmChecked || false);
      setVideoPreview(data.videoPreview);
      setThumbnailPreview(data.thumbnailPreview);
      setVideoName(data.videoName);
      setVideoFileType(data.videoFileType);
      setVideoSize(data.videoSize);
      setThumbnailName(data.thumbnailName);
      setThumbnailFileType(data.thumbnailFileType);
      setThumbnailSize(data.thumbnailSize);
      setVideoDuration(data.videoDuration);
      setVideoUploadComplete(true);
      setShowRemoveVideo(true);
      if (data.thumbnailName) setThumbnailUploadProgress(100);
    }
  }, []);

  const VideoTypeOptions = videoTypes.map((item) => ({
    value: item.id,
    label: item.dataName,
  }));

  const languageOptions = languages.map((item) => ({
    value: item.id,
    label: item.dataName,
  }));

  const contentCategoryOptions = [
    { value: "MOTION", label: "Motion" },
    { value: "STATIC", label: "Static" },
  ];

  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: "48px",
      borderRadius: "8px",
      borderColor: state.isFocused ? "#a855f7" : "#dbcfe8",
      backgroundColor: "#faf7fc",
      boxShadow: state.isFocused ? "0 0 0 3px rgba(168, 85, 247, 0.1)" : "none",
      "&:hover": {
        borderColor: "#a855f7",
      },
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: "0 12px",
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: "200px",
      overflowY: "auto",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#6b7280",
      fontSize: "14px",
    }),
    singleValue: (provided) => ({
      ...provided,
      fontSize: "14px",
      color: "#1f2937",
    }),
  };
  return (
    <div>
      {isUploading && (
        <div className="upload-loader-overlay">
          <div className="upload-loader" />
        </div>
      )}
      <Header />
      <div className="upload-video-container">
        <div className="upload-video-content">
          <h1 className="upload-page-title">Upload Video</h1>

          <form className="upload-form" onSubmit={handlePublish}>
            {/* Video File Section */}
            <div className="form-section">
              <label className="form-label">Video File</label>
              <p className="form-sublabel">
                Upload your video file ( MP4, MOV, WEBM, M4V, MKV supported)
              </p>

              {!videoPreview ? (
                <div
                  className={`upload-area ${errors.videoName ? "error-border" : ""}`}
                  onDrop={handleVideoDrop}
                  onDragOver={handleDragOver}
                >
                  <p className="upload-text">Drag and drop to upload</p>
                  <p className="upload-hint">
                    Drag and drop your video file here, or click to browse
                  </p>
                  <button
                    type="button"
                    className="browse-button"
                    onClick={() => videoInputRef.current?.click()}
                  >
                    Browse
                  </button>
                  <input
                    ref={videoInputRef}
                    type="file"
                    accept="video/*"
                    onChange={handleVideoChange}
                    style={{ display: "none" }}
                  />
                </div>
              ) : (
                <div className="video-preview-section">
                  <video
                    className="video-preview"
                    src={videoPreview}
                    controls
                    accept=".mp4,.mov,.webm,.m4v,.mkv"
                  />

                  {/* Show progress while upload is ongoing */}
                  {!videoUploadComplete && (
                    <div className="upload-progress-container">
                      <div className="video-progress-label">Uploading...</div>
                      <div className="video-progress-bar">
                        <div
                          className="video-progress-fill"
                          style={{ width: `${Math.min(uploadProgress, 100)}%` }}
                        />
                      </div>
                      <div className="video-progress-percentage">
                        {Math.round(Math.min(uploadProgress, 100))}%
                      </div>
                    </div>
                  )}

                  {showRemoveVideo && (
                    <button
                      type="button"
                      className="remove-video-button"
                      onClick={() => {
                        setVideoFile(null);
                        setVideoName("");
                        setVideoFileType("");
                        setVideoSize("");
                        setVideoPreview(null);
                        setUploadProgress(0);
                        setVideoUploadComplete(false);
                        setShowRemoveVideo(false);
                      }}
                    >
                      Remove
                    </button>
                  )}
                </div>
              )}
            </div>
            {errors.videoName && (
              <p className="field-error">{errors.videoName}</p>
            )}
            {/* YouTube Link Section */}
            <div className="form-section">
              <div>
                <div className="youtube-link-header">
                  <label className="form-label">
                    + Video Link (Add a video link for reference )
                  </label>
                  <img
                    src={ArrowRight}
                    alt="Arrow Right"
                    className="link-arrow"
                  />
                </div>

                <div className="youtube-link-input-container">
                  <button
                    type="button"
                    className="add-button-inside"
                    onClick={() => {
                      if (!videoName) {
                        setErrors((prev) => ({
                          ...prev,
                          videoName:
                            "Please upload a video before adding a video reference link.",
                        }));
                        return;
                      }

                      sessionStorage.setItem(
                        "uploadVideoDraft",
                        JSON.stringify({
                          title,
                          description,
                          caption,
                          keywords,
                          videoType,
                          language,
                          contentCategory,
                          confirmChecked,
                          videoPreview,
                          thumbnailPreview,
                          videoDuration,
                          videoName,
                          videoFileType,
                          videoSize,
                          thumbnailName,
                          thumbnailFileType,
                          thumbnailSize,
                        }),
                      );

                      navigate("/video-reference-editor", {
                        state: {
                          uploadedVideo: videoPreview,
                          videoDuration,
                        },
                      });
                    }}
                  >
                    Add Video Link
                  </button>
                </div>
              </div>
            </div>
            <div className="form-section">
              <label className="form-label">Add Keyword</label>

              <div
                className={`keyword-container ${errors.keywords ? "error-border" : ""}`}
              >
                {keywords.map((item, index) => (
                  <div key={index} className="keyword-chip">
                    <span>{item}</span>

                    <button
                      type="button"
                      className="keyword-remove"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeKeyword(index);
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}

                <input
                  type="text"
                  className="keyword-input"
                  placeholder={
                    keywords.length === 0 ? "Type keyword and press Enter" : ""
                  }
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                  onKeyDown={handleKeywordKeyDown}
                />
              </div>
            </div>
            {errors.keywords && (
              <p className="field-error">{errors.keywords}</p>
            )}

            {/* Thumbnail Section */}
            <div className="form-section">
              <label className="form-label">Thumbnail</label>

              {!thumbnailPreview ? (
                <div
                  className={`upload-area ${errors.thumbnailName ? "error-border" : ""}`}
                  onDrop={handleThumbnailDrop}
                  onDragOver={handleDragOver}
                >
                  <p className="upload-text">Drag and drop to upload</p>
                  <p className="upload-hint">
                    Drag and drop your Thumbnail file here, or click to browse
                  </p>
                  <button
                    type="button"
                    className="browse-button"
                    onClick={() => thumbnailInputRef.current?.click()}
                  >
                    Browse
                  </button>
                  <input
                    ref={thumbnailInputRef}
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    onChange={handleThumbnailChange}
                    style={{ display: "none" }}
                  />
                </div>
              ) : (
                <div className="thumbnail-preview-section">
                  {thumbnailPreview && (
                    <>
                      <img
                        src={ThumbnailDeleteIcon}
                        alt="delete"
                        className="thumbnail-delete-button"
                        onClick={() => setShowDeleteThumbnailModal(true)}
                      />

                      <img
                        src={thumbnailPreview}
                        alt="Thumbnail preview"
                        className="thumbnail-preview"
                      />
                      <div className="upload-progress-container">
                        {thumbnailUploadProgress < 100 ? (
                          <div className="thumbnail-uploaded-label">
                            Uploading...
                          </div>
                        ) : (
                          <div className="thumbnail-uploaded-label">
                            Thumbnail uploaded successfully
                          </div>
                        )}

                        <div className="video-progress-bar">
                          <div
                            className="video-progress-fill"
                            style={{
                              width: `${Math.min(thumbnailUploadProgress, 100)}%`,
                            }}
                          />
                        </div>
                        <div className="video-progress-percentage">
                          {Math.round(Math.min(thumbnailUploadProgress, 100))}%
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {errors.thumbnailName && (
              <p className="field-error">{errors.thumbnailName}</p>
            )}
            {/* Video Title */}
            <div className="form-section">
              <label className="form-label">Video Title</label>
              <input
                type="text"
                placeholder="Add a title that describes your video"
                className={`text-input ${errors.title ? "error-border" : ""}`}
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);

                  setErrors((prev) => ({
                    ...prev,
                    title: "",
                  }));
                }}
              />
            </div>

            {errors.title && <p className="field-error">{errors.title}</p>}
            {/* Video Description */}
            <div className="form-section">
              <label className="form-label">Video Description</label>
              <textarea
                placeholder="Tell viewers about your video"
                className={`textarea-input ${errors.description ? "error-border" : ""}`}
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);

                  setErrors((prev) => ({
                    ...prev,
                    description: "",
                  }));
                }}
                rows="5"
              />
            </div>
            {errors.description && (
              <p className="field-error">{errors.description}</p>
            )}

            {/* Add Caption */}
            <div className="form-section">
              <label className="form-label">Add Caption (CC)</label>
              <textarea
                placeholder="Write your caption..."
                className={`textarea-input ${errors.caption ? "error-border" : ""}`}
                value={caption}
                onChange={(e) => {
                  setCaption(e.target.value);

                  setErrors((prev) => ({
                    ...prev,
                    caption: "",
                  }));
                }}
                rows="5"
              />
            </div>
            {errors.caption && <p className="field-error">{errors.caption}</p>}

            {/* Video Type */}
            <div className="form-section">
              <label className="form-label">Video Type</label>
              <Select
                options={VideoTypeOptions}
                placeholder="Select a reel type"
                value={
                  VideoTypeOptions.find(
                    (option) => option.value === videoType,
                  ) || null
                }
                onChange={(selected) => {
                  setVideoType(selected?.value || "");

                  if (errors.videoType) {
                    setErrors((prev) => ({
                      ...prev,
                      videoType: "",
                    }));
                  }
                }}
                styles={{
                  ...customSelectStyles,
                  control: (provided, state) => ({
                    ...customSelectStyles.control(provided, state),
                    borderColor: errors.videoType
                      ? "#ef4444"
                      : state.isFocused
                        ? "#a855f7"
                        : "#dbcfe8",
                  }),
                }}
              />
            </div>
            {errors.videoType && (
              <p className="field-error">{errors.videoType}</p>
            )}
            <div className="form-section">
              <div className="category-label-row">
                <label className="form-label" htmlFor="video-content-category">
                  Content Category
                </label>
                <button
                  type="button"
                  className="category-info-button"
                  aria-label="Learn about content categories"
                  onClick={() => setShowCategoryHelp(true)}
                >
                  i
                </button>
              </div>
              <Select
                inputId="video-content-category"
                options={contentCategoryOptions}
                placeholder="Select Motion or Static"
                value={
                  contentCategoryOptions.find(
                    (option) => option.value === contentCategory,
                  ) || null
                }
                onChange={(selected) => {
                  setContentCategory(selected?.value || "");
                  if (errors.contentCategory) {
                    setErrors((prev) => ({ ...prev, contentCategory: "" }));
                  }
                }}
                styles={{
                  ...customSelectStyles,
                  control: (provided, state) => ({
                    ...customSelectStyles.control(provided, state),
                    borderColor: errors.contentCategory
                      ? "#ef4444"
                      : state.isFocused
                        ? "#a855f7"
                        : "#dbcfe8",
                  }),
                }}
              />
              {errors.contentCategory && (
                <p className="field-error">{errors.contentCategory}</p>
              )}
            </div>
            <div className="form-section">
              <label className="form-label">Video Language</label>
              <Select
                options={languageOptions}
                placeholder="Select a reel language"
                value={
                  languageOptions.find((option) => option.value === language) ||
                  null
                }
                onChange={(selected) => {
                  setLanguage(selected?.value || "");

                  if (errors.language) {
                    setErrors((prev) => ({
                      ...prev,
                      language: "",
                    }));
                  }
                }}
                styles={{
                  ...customSelectStyles,
                  control: (provided, state) => ({
                    ...customSelectStyles.control(provided, state),
                    borderColor: errors.language
                      ? "#ef4444"
                      : state.isFocused
                        ? "#a855f7"
                        : "#dbcfe8",
                  }),
                }}
              />
            </div>
            {errors.language && (
              <p className="field-error">{errors.language}</p>
            )}
            <div
              className={`youtube-link-header ${errors.consent ? "checkbox-error" : ""
                }`}
            >
              <input
                type="checkbox"
                name="rememberMe"
                className="video-checkbox-input"
                checked={confirmChecked}
                onChange={(e) => {
                  setConfirmChecked(e.target.checked);

                  setErrors((prev) => ({
                    ...prev,
                    consent: "",
                  }));
                }}
              />
              <label htmlFor="allow-comments" className="video-checkbox-label">
                I confirm this content is my own or I have rights to use it.
              </label>
            </div>
            {errors.consent && <p className="field-error">{errors.consent}</p>}

            {/* Form Buttons */}
            <div className="form-buttons">
              <button className="cancel-button" onClick={handleCancel}>
                Cancel
              </button>
              <button type="submit" className="publish-button">
                Publish
              </button>
            </div>
          </form>
        </div>

        {showCategoryHelp && (
          <div
            className="modal-overlay"
            onClick={() => setShowCategoryHelp(false)}
          >
            <div
              className="modal-content content-category-help-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="video-category-help-title"
              onClick={(event) => event.stopPropagation()}
            >
              <h2 id="video-category-help-title">
                Choosing a content category
              </h2>
              <p>
                <strong>Motion:</strong> Choose this when the video shows clear,
                meaningful movement or action, such as a car driving, someone
                walking, dancing, or exercising.
              </p>
              <p>
                <strong>Static:</strong> Choose this when the scene is mostly
                still. Small natural movements, such as head or hand gestures
                while someone speaks, are considered Static.
              </p>
              <button
                type="button"
                className="modal-submit-button"
                onClick={() => setShowCategoryHelp(false)}
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* Confirmation Modal */}
        {showConfirmModal && (
          <div className="modal-overlay">
            <div className="modal-content confirm-modal">
              <img src={ConfirmIconLogo} alt="logo" />
              <h2 className="modal-title">Submit Video for Approval?</h2>
              <p className="modal-message">
                Are you sure you want to submit this video for admin approval?
                Once approved, you won’t be able to edit or make changes to this
                video.
              </p>
              <div className="modal-buttons">
                <button
                  className="modal-cancel-button"
                  onClick={() => setShowConfirmModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="modal-submit-button"
                  onClick={handleConfirmSubmit}
                  disabled={isUploading}
                >
                  {isUploading ? "Submitting..." : "Submit"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Success Modal */}
        {showSuccessModal && (
          <div className="modal-overlay">
            <div className="modal-content success-modal">
              <img src={SuccessIcon} alt="success" />
              <h2 className="modal-title">
                Your Video has Been Submitted for Approval
              </h2>
              <p className="modal-message">
                The video has been submitted successfully and is under admin
                review. You can check its status anytime in the Content Awaiting
                Approval list.
              </p>
              <div className="modal-buttons">
                <button className="modal-done-button" onClick={handleDone}>
                  okay
                </button>
                <button className="modal-submit-button" onClick={handleview}>
                  View Approval list
                </button>
              </div>
            </div>
          </div>
        )}

        {showDeleteThumbnailModal && (
          <div className="modal-overlay">
            <div className="modal-content delete-thumbnail-modal">
              <div className="delete-icon-wrapper">
                <img src={ConfirmDelete} alt="delete" className="confirm-del" />
              </div>

              <h2 className="video-delete-confirm-title">
                Are you sure you want to
                <br />
                delete this thumbnail ?
              </h2>

              <div className="modal-buttons">
                <button
                  className="del-cancel-button"
                  onClick={() => setShowDeleteThumbnailModal(false)}
                >
                  Cancel
                </button>

                <button
                  className="del-submit-button"
                  onClick={handleDeleteThumbnail}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
